import type { Dayjs } from 'dayjs';
import type { ContractedQuoteInfoOutDTO } from '@api/ringil-api.v2.tsx';

/**
 * Carrier side of the CONTRACTED ("Smluvní dopravce") distribution. The carrier never sets a price, he only
 * accepts the whole shipment (no laden factor) or N units (laden factor). The backend recomputes the price,
 * these helpers only compute what we show and what we send to satisfy MakeOfferInDTO validation.
 */

const isSet = (value: number | null | undefined): value is number => value !== null && value !== undefined;

const round4 = (value: number): number => Math.round(value * 10000) / 10000;

/**
 * Units the carrier can pick when a laden factor is set: every multiple of LF up to the remaining units
 * (only with partial delivery) plus the remaining units themselves, which are always valid.
 */
export const contractedUnitOptions = (
  remainingUnits: number | null | undefined,
  ladenFactor: number | null | undefined,
  allowPartialDelivery: boolean,
): number[] => {
  if (!remainingUnits || remainingUnits <= 0 || !ladenFactor || ladenFactor <= 0) return [];
  if (!allowPartialDelivery) return [remainingUnits];
  const options: number[] = [];
  for (let i = ladenFactor; i <= remainingUnits; i += ladenFactor) options.push(i);
  if (!options.includes(remainingUnits)) options.push(remainingUnits);
  return options;
};

/** Same rule as the backend: equal to the remaining units, or (partial delivery only) a multiple of LF up to them. */
export const isValidContractedUnits = (
  units: number | null | undefined,
  remainingUnits: number | null | undefined,
  ladenFactor: number | null | undefined,
  allowPartialDelivery: boolean,
): boolean => {
  if (!isSet(units) || units <= 0 || !remainingUnits || !ladenFactor) return false;
  if (units === remainingUnits) return true;
  return allowPartialDelivery && units % ladenFactor === 0 && units <= remainingUnits;
};

/** Currency of the contracted offer; falls back to the first row that has one. */
export const contractedCurrency = (contracted: ContractedQuoteInfoOutDTO | null | undefined): string | undefined =>
  contracted?.currency ?? contracted?.rows?.find(r => !!r.currency)?.currency;

/** A row without a unit price means the carrier has no contracted price for that manipulation unit. */
export const hasMissingContractedPrice = (contracted: ContractedQuoteInfoOutDTO | null | undefined): boolean =>
  !contracted || !contracted.rows?.length || contracted.rows.some(r => !isSet(r.unitPrice));

/**
 * Total price of the offer.
 * - Without laden factor (or no units): the whole-shipment total computed by the backend.
 * - With laden factor: units × unit price (the shipment has a single manipulation unit in that case).
 * Null when it cannot be computed (missing price row).
 */
export const contractedTotal = (
  contracted: ContractedQuoteInfoOutDTO | null | undefined,
  ladenFactor: number | null | undefined,
  units: number | null | undefined,
): number | null => {
  if (!contracted || hasMissingContractedPrice(contracted)) return null;
  if (!ladenFactor || !isSet(units)) return isSet(contracted.totalPrice) ? round4(contracted.totalPrice) : null;
  if (contracted.rows.length === 1) return round4(units * contracted.rows[0].unitPrice);
  // Mixed units together with a laden factor are rejected by the backend; prorate so we still show something sane.
  if (!contracted.totalUnits || !isSet(contracted.totalPrice)) return null;
  return round4((contracted.totalPrice * units) / contracted.totalUnits);
};

/** Unit price used for the "units × unit price" line, defined only when there is exactly one row. */
export const contractedSingleUnitPrice = (contracted: ContractedQuoteInfoOutDTO | null | undefined): number | null =>
  contracted?.rows?.length === 1 && isSet(contracted.rows[0].unitPrice) ? contracted.rows[0].unitPrice : null;

/**
 * MakeOfferInDTO needs a future validTill; the backend forces it to the distribution deadline anyway.
 * Use the deadline while it is still ahead of us, otherwise now + 1 hour.
 */
export const contractedValidTill = (distributionEnd: Dayjs | null | undefined, now: Dayjs): Dayjs =>
  distributionEnd && distributionEnd.isAfter(now.add(1, 'minute')) ? distributionEnd : now.add(1, 'hour');
